import { HttpResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, takeUntil, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { Router, NavigationStart, NavigationEnd } from '@angular/router';
import { ReportService } from '../api/report.service';
import { ToastrNotificationService } from './toastr-notification.service';
import { DownloadReportRequest } from '../../interfaces/api/report.interface';

@Injectable({
  providedIn: 'root',
})
export class ReportDownloadService implements OnDestroy {
  private _isExporting$ = new BehaviorSubject<boolean>(false);
  public isExporting$ = this._isExporting$.asObservable();
  private beforeUnloadListener: ((event: BeforeUnloadEvent) => void) | null =
    null;
  private routerSubscription: Subscription | null = null;
  private allowNavigation: boolean = false;

  constructor(
    private _reportService: ReportService,
    private _notificationService: ToastrNotificationService,
    private _translateService: TranslateService,
    private _router: Router
  ) {}

  ngOnDestroy(): void {
    this.allowPageExit();
  }

  public validateExportRequest(request: DownloadReportRequest): boolean {
    const hasStartDate =
      request.startDate ||
      (request.filters && (request.filters as any).dateStart);
    const hasEndDate =
      request.endDate || (request.filters && (request.filters as any).dateEnd);

    const isRangeWithoutDates =
      request.reportType === 'range' && (!hasStartDate || !hasEndDate);

    if (isRangeWithoutDates) {
      this.showValidationError('export.validation.dateRangeRequired');
      return false;
    }

    return true;
  }

  public setExportingState(isExporting: boolean): void {
    this._isExporting$.next(isExporting);

    if (isExporting) {
      this.preventPageExit();
    } else {
      this.allowPageExit();
    }
  }

  public get isExporting(): boolean {
    return this._isExporting$.value;
  }

  private preventPageExit(): void {
    this.beforeUnloadListener = (event: BeforeUnloadEvent) => {
      const message = this._translateService.instant(
        'export.downloadInProgress'
      );
      event.preventDefault();
      event.returnValue = message;
      return message;
    };

    window.addEventListener('beforeunload', this.beforeUnloadListener);

    this.routerSubscription = this._router.events.subscribe((event) => {
      if (
        event instanceof NavigationStart &&
        this.isExporting &&
        !this.allowNavigation
      ) {
        const confirmNavigation = window.confirm(
          this._translateService.instant('export.downloadInProgress')
        );

        if (confirmNavigation) {
          this.allowNavigation = true;
          this.setExportingState(false);
        } else {
          this._router.navigateByUrl(this._router.url);
        }
      } else if (event instanceof NavigationEnd) {
        this.allowNavigation = false;
      }
    });
  }

  private allowPageExit(): void {
    if (this.beforeUnloadListener) {
      window.removeEventListener('beforeunload', this.beforeUnloadListener);
      this.beforeUnloadListener = null;
    }

    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
      this.routerSubscription = null;
    }

    this.allowNavigation = false;
  }

  public buildDownloadRequest(
    request: DownloadReportRequest
  ): DownloadReportRequest {
    const startDate =
      request.startDate ||
      (request.filters && (request.filters as any).dateStart) ||
      (request.filters && (request.filters as any).startDate) ||
      (request.filters && (request.filters as any).fromDate);

    const endDate =
      request.endDate ||
      (request.filters && (request.filters as any).dateEnd) ||
      (request.filters && (request.filters as any).endDate) ||
      (request.filters && (request.filters as any).toDate);

    const filters = request.filters ? { ...request.filters } : {};

    if (filters) {
      delete filters.dateStart;
      delete filters.dateEnd;
      delete filters.fromDate;
      delete filters.toDate;
    }

    return {
      reportType: request.reportType,
      dataType: request.dataType,
      module: request.module,
      startDate: startDate,
      endDate: endDate,
      limit: request.limit,
      page: request.page,
      sortProp: request['sortProp'],
      sortDir: request['sortDir'],
      count: request['count'],
      filters: filters,
    };
  }

  public executeReportDownload(
    downloadRequest: DownloadReportRequest,
    unsubscribe$: Observable<any>
  ): Observable<any> {
    this.setExportingState(true);
    this.showDownloadStartNotification();

    return new Observable((observer) => {
      this._reportService
        .downloadReport(downloadRequest)
        .pipe(takeUntil(unsubscribe$))
        .subscribe({
          next: async (response) => {
            await this.handleFileResponse(response);
            this.setExportingState(false);
            observer.next(response);
            observer.complete();
          },
          error: (error) => {
            this.handleDownloadError();
            this.setExportingState(false);
            observer.error(error);
          },
        });
    });
  }

  // The backend sends the file as binary. An empty report has no file and comes
  // back as the usual JSON envelope instead, handled by handleDownloadResponse.
  public async handleFileResponse(response: HttpResponse<Blob>): Promise<void> {
    const blob = response.body;
    const contentType = response.headers.get('Content-Type') || '';

    if (!blob) {
      this.showErrorNotification('export.downloadFailed');
      return;
    }

    if (contentType.includes('application/json')) {
      this.handleDownloadResponse(JSON.parse(await blob.text()));
      return;
    }

    const filename =
      this.getFilenameFromDisposition(
        response.headers.get('Content-Disposition')
      ) || 'report.xlsx';
    this.handleDownloadResult(this.executeFileDownload(blob, filename));
    this.showSuccessNotification('export.downloadStarted');
  }

  private getFilenameFromDisposition(disposition: string | null): string | null {
    if (!disposition) return null;
    const encoded =
      disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1] ??
      disposition.match(/filename="?([^";]+)"?/i)?.[1];
    if (!encoded) return null;
    try {
      return decodeURIComponent(encoded);
    } catch {
      return encoded;
    }
  }

  public handleDownloadResponse(response: any): void {
    const isSuccessResponse = response?.status === 'success' && response?.data;

    if (isSuccessResponse) {
      this.processFileDownload(response.data);
      this.showSuccessNotification('export.downloadStarted');
    } else {
      this.showErrorNotification('export.downloadFailed');
    }
  }

  public handleDownloadError(): void {
    this.showErrorNotification('export.downloadError');
  }

  public processFileDownload(data: any): void {
    const downloadResult = this.attemptFileDownload(data);
    this.handleDownloadResult(downloadResult);
  }

  public attemptFileDownload(data: any): { success: boolean; error?: string } {
    const fileInfo = this.determineFileType(data);
    const blobResult = this.safeBlobCreation(data, fileInfo.mimeType);

    return blobResult.success
      ? this.executeFileDownload(
          blobResult.blob!,
          data.filename || fileInfo.defaultFilename
        )
      : blobResult;
  }

  public determineFileType(data: any): {
    mimeType: string;
    extension: string;
    defaultFilename: string;
  } {
    const isCSV = data.filename?.endsWith('.csv');

    return isCSV
      ? {
          mimeType: 'text/csv;charset=utf-8',
          extension: 'csv',
          defaultFilename: 'report.csv',
        }
      : {
          mimeType: 'application/vnd.ms-excel',
          extension: 'xls',
          defaultFilename: 'report.xls',
        };
  }

  public safeBlobCreation(
    data: any,
    mimeType: string
  ): { success: boolean; blob?: Blob; error?: string } {
    const hasValidBuffer =
      data?.buffer?.data && Array.isArray(data.buffer.data);

    return hasValidBuffer
      ? { success: true, blob: this.createBlobFromData(data, mimeType) }
      : {
          success: false,
          error: this._translateService.instant('export.invalidFileData'),
        };
  }

  public createBlobFromData(data: any, mimeType: string): Blob {
    const bufferData = new Uint8Array(data.buffer.data);
    return new Blob([bufferData], { type: mimeType });
  }

  public executeFileDownload(
    blob: Blob,
    filename: string
  ): { success: boolean; error?: string } {
    this.downloadFile(blob, filename);
    return { success: true };
  }

  public downloadFile(blob: Blob, filename: string): void {
    const url = this.createDownloadUrl(blob);
    const downloadLink = this.createDownloadLink(url, filename);

    this.executeDownload(downloadLink);
    this.cleanupDownload(downloadLink, url);
  }

  public createDownloadUrl(blob: Blob): string {
    return window.URL.createObjectURL(blob);
  }

  public createDownloadLink(url: string, filename: string): HTMLAnchorElement {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener noreferrer');
    return link;
  }

  public executeDownload(link: HTMLAnchorElement): void {
    document.body.appendChild(link);
    link.click();
  }

  public cleanupDownload(link: HTMLAnchorElement, url: string): void {
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);
  }

  public handleDownloadResult(result: {
    success: boolean;
    error?: string;
  }): void {
    const notificationMessage = result.success
      ? this._translateService.instant('export.fileDownloadedSuccessfully')
      : result.error ||
        this._translateService.instant('export.fileProcessingError');

    const notificationType = result.success ? 'success' : 'error';

    this._notificationService.showNotification({
      type: notificationType,
      message: notificationMessage,
    });
  }

  private showValidationError(message: string): void {
    this._notificationService.showNotification({
      type: 'warning',
      message,
    });
  }

  private showDownloadStartNotification(): void {
    const downloadMessage = this._translateService.instant(
      'export.downloadStarted'
    );
    const tabMessage = this._translateService.instant('export.dontChangeTab');
    const fullMessage = `${downloadMessage}. ${tabMessage}`;

    this._notificationService.showNotification({
      type: 'info',
      message: fullMessage,
    });
  }

  private showSuccessNotification(message: string): void {
    this._notificationService.showNotification({
      type: 'success',
      message,
    });
  }

  private showErrorNotification(message: string): void {
    this._notificationService.showNotification({
      type: 'error',
      message,
    });
  }
}
